import {
  CardContent,
  Grid,
  Typography,
  styled,
  CardContentProps,
  Paper,
  TextField,
  Box,
  useTheme,
} from "@mui/material";
import { IHotel } from "@vmosolutions/oc-interfaces";
import { Fragment, useEffect, useRef, useState } from "react";
import { DraggableListItem } from "../../../components/DraggableListItem/DraggableListItem"; // API Calls
import BackspaceIcon from "@mui/icons-material/Backspace";
import AmplifyAPIWrapper from "../../../API/AmplifyAPIWrapper";
import { IconButton } from "../../../components/IconButton/IconButton";
import { SpinnerProgress } from "../../../components/SpinnerProgress/SpinnerProgress";
// Accordion Forms
import { useDispatch, useSelector } from "react-redux";
import { gotHotels } from "../../../redux/actions/CrewTravel/CrewTravel.Actions";
import { IRootState } from "../../../redux/reducers/CombinedReducers";
import { IActiveModal } from "../../Admin/AdminWindow";
import { HotelCard } from "./Hotel/HotelCard";

interface IManageHotelsProps {
  id: string;
  setActiveModal: (activeModal: IActiveModal) => void;
}

// Takes in a list of hotels and outputs them grouped by icao sorted by priority (location).
export const groupedHotelList = (hotels: IHotel[]): any => {
  if (hotels.length > 0) {
    // Place hotels in respective icao groups.
    const hotelsByIcao = hotels.reduce((obj: { [key: string]: any }, hotel) => {
      const hotelIcao = hotel.icao;
      if (!obj[hotelIcao]) {
        obj[hotelIcao] = [hotel];
      } else {
        obj[hotelIcao].push(hotel);
      }
      return obj;
    }, {});

    // Create icao grouped options
    const groupedOptions = [];
    for (const [hotelIcao, hotelList] of Object.entries(hotelsByIcao)) {
      // const airportLocation = await findAirports(hotelIcao);
      groupedOptions.push({
        label: `Location: ${hotelIcao}`,
        options: (hotelList as IHotel[]).sort((a, b) => a.priority - b.priority),
      });
    }

    return groupedOptions;
  } else {
    return [];
  }
};

const CardStyles: React.FC<CardContentProps> = styled(CardContent)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  padding: 16,
  paddingBottom: "16px !important",

  "& .form_container": {
    height: "100%",
    "& form": {
      width: "100%",
    },
    "& .form_title": {
      padding: "4px 0px",
    },
  },
  "& .form_title": {
    margin: "0px 10px",
    color: theme.custom.forms.labels,
    "& .deselect_btn": {
      margin: "0px 5px",
      color: theme.custom.forms.labels,
      padding: 0,
      borderRadius: 3,
      verticalAlign: "sub",
    },
  },
  "& .left_pane": {
    height: "100%",
    width: 230,
    "& .react-select__menu": {
      maxHeight: "auto",
    },
  },
  "& .right_pane": {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    marginLeft: 15,
    width: "calc(100% - 230px)",
    "& .content": {
      color: "red",
      height: "calc(100% - 40px)",
      display: "flex",
      flexDirection: "column",
      overflowY: "auto",
    },
  },
}));

export const ManageHotels = (props: IManageHotelsProps): JSX.Element => {
  const { setActiveModal } = props;
  const dispatch = useDispatch();
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchFilter, setSearchFilter] = useState<string>("");

  /** Get height of the right panel and apply to max menu size */
  const contentRef = useRef<HTMLDivElement>();
  const { height } = contentRef?.current?.getBoundingClientRect() ?? { height: 0 };

  /////////////////////////
  // MANAGE HOTEL LIST
  /////////////////////////
  const hotels = useSelector((state: IRootState) => state.crewTravel.hotels);
  const [hotelGroups, setHotelGroups] = useState<any[]>([]);
  const [selectedHotel, setSelectedHotel] = useState<IHotel>(null);

  useEffect(() => {
    const getHotelsAction = async (): Promise<void> => {
      //const notifications: INotifications = { dispatch, errorMessage: "No Hotel found." };
      setIsLoading(true);
      const response = await AmplifyAPIWrapper.Get<IHotel[]>("/hotels");
      if (Array.isArray(response)) {
        dispatch(gotHotels(response.sort((a, b) => (b.name > a.name ? -1 : 1))));
      }
    };
    getHotelsAction();
  }, [dispatch]);

  useEffect(() => {
    setHotelGroups(groupedHotelList(hotels));
    setIsLoading(false);
  }, [hotels]);

  // If users create, updated or delete hotel, update list.
  const updateHotelList = (hot: IHotel, mode: "create" | "update" | "delete"): void => {
    const newHotels = [...hotels];
    const index = newHotels.findIndex((hotel: IHotel) => hotel._id === hot._id);
    switch (true) {
      case hot._id && mode === "create": // Hotel Created
        newHotels.push(hot);
        setSelectedHotel(hot);
        break;
      case hot._id && mode === "update" && index > -1: // Hotel Updated
        newHotels[index] = hot;
        setSelectedHotel(hot);
        break;
      case hot._id && mode === "delete" && index > -1: // Hotel Deleted
        newHotels.splice(index, 1);
        setSelectedHotel(null);
        break;
      default:
      // code block
    }
    dispatch(gotHotels(newHotels.sort((a, b) => (b.name > a.name ? -1 : 1))));
  };

  return (
    <CardStyles>
      <SpinnerProgress className={"loadingSpinner"} loading={isLoading} message="loading hotel data" />

      <Grid container direction="row" wrap="nowrap" className="form_container">
        <Grid item xs={2} className="left_pane">
          <Paper elevation={4} sx={{ pl: 1, pb: 1, width: "100%" }}>
            <Grid item xs={12}>
              <TextField
                placeholder="Filter hotel by name"
                variant="outlined"
                size="small"
                fullWidth
                onChange={(e) => {
                  const val = e.target.value;
                  setSearchFilter(val);
                }}
              />
            </Grid>
            <Grid container item xs={12}>
              <Grid item sx={{ overflowY: "scroll", height: "67vh", width: "100%" }}>
                {hotelGroups.map((location, key) => {
                  const filter = searchFilter.toLowerCase().replace(/[^a-zA-Z0-9\s]/g, "");
                  const filteredHotels = location.options.filter((hotel: any) => {
                    return hotel.name.toLowerCase().includes(filter);
                  });
                  if (filteredHotels?.length == 0) return <Fragment key={key}></Fragment>;
                  const items = [
                    <DraggableListItem
                      key={key}
                      active={false}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: theme.custom.forms.labels,
                        color: "#111",
                      }}
                    >
                      <Box sx={{ pl: 1 }}>{location.label}</Box>
                    </DraggableListItem>,
                  ];
                  filteredHotels.forEach((hotel: any, hotelKey: string) => {
                    items.push(
                      <DraggableListItem
                        key={`${key}-${hotelKey}`}
                        active={selectedHotel?._id === hotel?._id ? true : false}
                        onClick={() => setSelectedHotel(hotel)}
                        sx={{
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Box sx={{ pl: 1 }}>{`${hotel.priority} - ${hotel.name}`}</Box>
                      </DraggableListItem>
                    );
                  });
                  return <Fragment key={key}>{items}</Fragment>;
                })}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={10} className="right_pane">
          <Typography variant="h6" className="form_title">
            Manage Hotel
            {selectedHotel && (
              <span className="form_subtitle">
                ({`${selectedHotel.name}`})
                <IconButton className="deselect_btn" onClick={() => setSelectedHotel(null)} size="large">
                  <BackspaceIcon />
                </IconButton>
              </span>
            )}
          </Typography>

          <Grid className="content" ref={contentRef}>
            <HotelCard
              selectedHotel={selectedHotel}
              updateHotelList={updateHotelList}
              setActiveModal={setActiveModal}
            />
          </Grid>
        </Grid>
      </Grid>
    </CardStyles>
  );
};
